import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send, CheckCircle } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import ScrollReveal from '../components/ScrollReveal';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    setTimeout(() => {
      console.log('Form submitted:', formData);
      toast({
        title: 'Message Sent!',
        description: 'Thank you for contacting us. We\'ll get back to you soon.',
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        message: ''
      });
      setIsSubmitting(false);
    }, 1000);
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Visit Us',
      details: ['AARTEMA', '1st Floor, C-26C, Main Road', 'Block F, Mansarover Garden', 'New Delhi, Delhi, 110015'],
      color: 'from-blue-500 to-blue-600'
    },
    {
      icon: Phone,
      title: 'Call Us',
      details: ['+91-9350056070', '+91-9289940000'],
      color: 'from-green-500 to-green-600'
    },
    {
      icon: Mail,
      title: 'Email Us',
      details: ['vikas.mangla@outlook.com'],
      color: 'from-purple-500 to-purple-600'
    },
    {
      icon: Clock,
      title: 'Working Hours',
      details: ['Tue - Sun: 9:00 AM - 7:00 PM', 'Monday: Closed'],
      color: 'from-orange-500 to-orange-600'
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#222] via-[#2a2a2a] to-[#1a1a1a] text-white py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle, #C1A57B 1px, transparent 1px)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        
        <div className="absolute top-10 right-10 w-72 h-72 bg-[#C1A57B]/10 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-10 left-10 w-96 h-96 bg-[#C1A57B]/5 rounded-full blur-3xl animate-float-delayed"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h1 className="text-6xl font-bold mb-4">Contact Us</h1>
          <div className="w-24 h-1.5 bg-gradient-to-r from-transparent via-[#C1A57B] to-transparent mx-auto mb-6"></div>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Have a question or ready to transform your workspace? We'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-20 bg-gradient-to-b from-[#EDEDED] to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => (
              <ScrollReveal key={index} delay={index * 100}>
                <div className="group bg-white p-6 rounded-2xl shadow-md hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 overflow-hidden relative">
                  {/* Animated gradient background */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${info.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}></div>
                  
                  <div className="relative z-10">
                    <div className={`w-14 h-14 bg-gradient-to-br ${info.color} rounded-xl flex items-center justify-center mb-4 transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg`}>
                      <info.icon className="text-white" size={24} />
                    </div>
                    <h3 className="text-lg font-bold text-[#222] mb-3">{info.title}</h3>
                    <div className="space-y-1">
                      {info.details.map((detail, idx) => (
                        <p key={idx} className="text-gray-600 text-sm leading-relaxed">
                          {detail}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Map Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <ScrollReveal direction="right">
              <div>
                <h2 className="text-4xl font-bold text-[#222] mb-4">Send Us a Message</h2>
                <div className="w-20 h-1.5 bg-gradient-to-r from-[#C1A57B] to-transparent mb-6"></div>
                <p className="text-gray-600 mb-8 text-lg">
                  Fill out the form below and we'll get back to you as soon as possible.
                </p>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="group">
                      <label htmlFor="name" className="block text-sm font-semibold text-[#222] mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border-2 border-[#EDEDED] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C1A57B] focus:border-transparent transition-all duration-300 hover:border-[#C1A57B]/30"
                        placeholder="Your name"
                      />
                    </div>
                    <div className="group">
                      <label htmlFor="email" className="block text-sm font-semibold text-[#222] mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border-2 border-[#EDEDED] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C1A57B] focus:border-transparent transition-all duration-300 hover:border-[#C1A57B]/30"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="group">
                      <label htmlFor="phone" className="block text-sm font-semibold text-[#222] mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border-2 border-[#EDEDED] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C1A57B] focus:border-transparent transition-all duration-300 hover:border-[#C1A57B]/30"
                        placeholder="+91-XXXX-XXXXXX"
                      />
                    </div>
                    <div className="group">
                      <label htmlFor="company" className="block text-sm font-semibold text-[#222] mb-2">
                        Company Name
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border-2 border-[#EDEDED] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C1A57B] focus:border-transparent transition-all duration-300 hover:border-[#C1A57B]/30"
                        placeholder="Your company"
                      />
                    </div>
                  </div>

                  <div className="group">
                    <label htmlFor="message" className="block text-sm font-semibold text-[#222] mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="w-full px-4 py-3 border-2 border-[#EDEDED] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C1A57B] focus:border-transparent transition-all duration-300 resize-none hover:border-[#C1A57B]/30"
                      placeholder="Tell us about your requirements..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="group w-full md:w-auto px-10 py-4 bg-gradient-to-r from-[#C1A57B] to-[#B09568] text-white font-bold rounded-xl hover:shadow-2xl hover:shadow-[#C1A57B]/50 transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <span>Send Message</span>
                        <Send className="group-hover:translate-x-1 transition-transform duration-300" size={20} />
                      </>
                    )}
                  </button>
                </form>
              </div>
            </ScrollReveal>

            {/* Map Placeholder */}
            <ScrollReveal direction="left">
             <div>
               <h2 className="text-4xl font-bold text-[#222] mb-4">Find Us</h2>
               <div className="w-20 h-1.5 bg-gradient-to-r from-[#C1A57B] to-transparent mb-6"></div>
               <div className="relative bg-gradient-to-br from-[#EDEDED] to-white rounded-2xl h-[600px] shadow-lg overflow-hidden">
                 <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3501.6855015906312!2d77.13092737563716!3d28.639186483778335!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390d031c4fffffff%3A0x66a52bb3b1762765!2sAARTEMA!5e0!3m2!1sen!2sin!4v1765442303949!5m2!1sen!2sin"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
               ></iframe>
             </div>
            </div>
          </ScrollReveal>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#222] via-[#2a2a2a] to-[#1a1a1a] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle, #C1A57B 1px, transparent 1px)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <ScrollReveal>
            <h2 className="text-4xl font-bold mb-4">Ready to Get Started?</h2>
            <p className="text-gray-300 text-lg mb-8">
              Request a free consultation and let us help you create the perfect workspace
            </p>
            <a
              href="tel:+91XXXXXXXXXX"
              className="inline-flex items-center px-10 py-4 bg-gradient-to-r from-[#C1A57B] to-[#B09568] text-white font-bold rounded-xl hover:shadow-2xl hover:shadow-[#C1A57B]/50 transition-all duration-300 transform hover:scale-105"
            >
              <Phone className="mr-2" size={20} />
              Call Now
            </a>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
};

export default Contact;
